import { INTEGRATION_PROVIDER_LABELS } from '@ashniva/types';
import { EmptyState, PageHeader, Tabs } from '@ashniva/ui';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { useProjectLinksQuery } from '../api';
import { AskPanel } from '../components/AskPanel';
import { ChangesPanel } from '../components/ChangesPanel';
import { CodeDrawer } from '../components/CodeDrawer';
import { FilesPanel } from '../components/FilesPanel';
import { NoAccess } from '../components/NoAccess';
import { OverviewPanel } from '../components/OverviewPanel';
import { ProblemNotice } from '../components/ProblemNotice';
import { StateBadge } from '../components/RepositoryStateView';
import { SearchPanel } from '../components/SearchPanel';
import { SymbolsPanel } from '../components/SymbolsPanel';
import { useRepositoryIntelligenceAccess } from '../ri-access';
import { isServed } from '../ri-state';
import { useRepositoryState } from '../use-repository-state';
import { WorkspaceContext, type CodeTarget, type Workspace } from '../workspace-context';

import '../repository-intelligence.css';

type Tab = 'overview' | 'search' | 'files' | 'symbols' | 'ask' | 'changes';

/**
 * One linked repository in Repository Intelligence: its state, search, files and symbols, questions and change
 * analyses (MASTER-SPEC §14, P14-02 to P14-08). Reached from a project's Code tab or the administrators' list. The
 * section and the open file, question or analysis are in the URL — ids, a path and line numbers, never code.
 */
export function RepositoryWorkspacePage() {
  const access = useRepositoryIntelligenceAccess();
  const { id: projectId = '', linkId = '' } = useParams();
  if (!access.query) {
    return <NoAccess />;
  }
  return <WorkspaceBody projectId={projectId} linkId={linkId} />;
}

function WorkspaceBody({ projectId, linkId }: { projectId: string; linkId: string }) {
  const access = useRepositoryIntelligenceAccess();
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState<CodeTarget | null>(null);
  const links = useProjectLinksQuery(projectId);
  const link = links.data?.find((entry) => entry.id === linkId);
  const repository = useRepositoryState(linkId);

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'search', label: 'Search' },
    { key: 'files', label: 'Files' },
    { key: 'symbols', label: 'Symbols' },
    ...(access.ask ? [{ key: 'ask' as const, label: 'Ask' }] : []),
    ...(access.analyze ? [{ key: 'changes' as const, label: 'Changes' }] : []),
  ];
  const requested = params.get('tab') as Tab | null;
  const tab = tabs.some((item) => item.key === requested) && requested ? requested : 'overview';

  const workspace = useMemo<Workspace>(
    () => ({
      linkId,
      projectId,
      openCode: setCode,
      filesHref: ({ path, start, end }) => {
        const next = new URLSearchParams({ tab: 'files', path });
        if (start) next.set('start', String(start));
        if (end && end !== start) next.set('end', String(end));
        return `?${next.toString()}`;
      },
    }),
    [linkId, projectId],
  );

  if (links.isSuccess && !link) {
    return (
      <EmptyState
        icon="?"
        title="This repository is not linked to the project"
        description="It may have been unlinked. Go back to the project to see its repositories."
        action={
          <Link to={`/projects/${encodeURIComponent(projectId)}?tab=code`}>
            Back to the project
          </Link>
        }
      />
    );
  }

  const title = link ? `${link.owner}/${link.name}` : 'Repository';
  let body: ReactNode;
  if (tab === 'overview') {
    body = <OverviewPanel repository={repository} />;
  } else if (repository.error) {
    body = <ProblemNotice error={repository.error} onRetry={repository.checkAgain} />;
  } else if (repository.state && !isServed(repository.state)) {
    body = (
      <EmptyState
        icon="⧗"
        title="Not available until the repository is indexed"
        description="Search, files, symbols, questions and analyses work once Repository Intelligence has indexed it."
        action={<Link to="?tab=overview">See where it is</Link>}
      />
    );
  } else {
    body = <Section tab={tab} />;
  }

  return (
    <WorkspaceContext.Provider value={workspace}>
      <div className="ri-page">
        <PageHeader
          crumbs={
            <>
              <Link to="/projects">Projects</Link> /{' '}
              <Link to={`/projects/${encodeURIComponent(projectId)}?tab=code`}>
                {link?.projectCode ?? 'Project'}
              </Link>{' '}
              / Code
            </>
          }
          title={title}
          subtitle={
            link
              ? `${INTEGRATION_PROVIDER_LABELS[link.provider]} · default branch ${link.defaultBranch}`
              : undefined
          }
          actions={repository.state ? <StateBadge kind={repository.state.kind} /> : undefined}
        >
          <Tabs
            aria-label="Repository sections"
            value={tab}
            onChange={(next) => setParams({ tab: next })}
            items={tabs}
          />
        </PageHeader>
        {body}
        <CodeDrawer target={code} onClose={() => setCode(null)} />
      </div>
    </WorkspaceContext.Provider>
  );
}

function Section({ tab }: { tab: Tab }) {
  switch (tab) {
    case 'search':
      return <SearchPanel />;
    case 'files':
      return <FilesPanel />;
    case 'symbols':
      return <SymbolsPanel />;
    case 'ask':
      return <AskPanel />;
    case 'changes':
      return <ChangesPanel />;
    default:
      return null;
  }
}
